import { Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'chart.js';
import { Subscription } from 'rxjs';
import { DataService, RouteService, SnackbarService } from '@/shared/services';
import { FticCourseTrendData } from '../../../../../server/types';

@Component({
  selector: 'ftic-course-dwf-apr-content',
  templateUrl: './ftic-course-dwf-apr-content.component.html',
  styleUrl: './ftic-course-dwf-apr-content.component.css',
  standalone: false,
})
export class CoursesAnalysisContentComponent implements OnInit, OnDestroy {
  public dataPending!: boolean;
  public fticCourseDwfAprChart!: Chart;
  public aprFticCourseGradesChart!: Chart;
  public passRateEnrollmentChart!: Chart;
  public data!: FticCourseTrendData;
  private subscription: Subscription = new Subscription();

  constructor(
    public dataService: DataService,
    private snackbarService: SnackbarService,
    private routeService: RouteService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.dataService.getData().subscribe({
        next: (dataStream) => {
          if (dataStream.data) {
            this.data = dataStream.data as FticCourseTrendData;
            this.generateCharts();
          }

          dataStream.error &&
            this.snackbarService.open(dataStream.error.message, 'error');
        },
      }),
    );
    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  generateCharts() {
    this.fticCourseDwfAprChart && this.fticCourseDwfAprChart.destroy();
    this.aprFticCourseGradesChart && this.aprFticCourseGradesChart.destroy();
    this.passRateEnrollmentChart && this.passRateEnrollmentChart.destroy();

    setTimeout(() => {
      this.fticCourseDwfAprChart = new Chart('ftic-course-dwf-apr-chart', {
        type: 'bar',
        data: {
          labels: this.data.ftic_dwf_courses.map((c) => c.course),
          datasets: [
            {
              label: 'Passed',
              data: this.data.ftic_dwf_courses.map((c) => c.num_passed),
              backgroundColor: 'rgba(0, 156, 222, 0.6)',
              borderColor: 'rgba(0, 156, 222, 1)',
              borderWidth: 1,
            },
            {
              label: 'Failed',
              data: this.data.ftic_dwf_courses.map((c) => c.num_failed),
              backgroundColor: 'rgba(255, 99, 132, 0.6)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              display: false,
              //   color: '#000', // Text color
              //   anchor: 'center', // Position relative to bar (start, center, end)
              //   align: 'top', // Align text (top, middle, bottom)
              //   rotation: -90,
              //   formatter(value, context) {
              //     const total = context.chart.data.datasets.reduce(
              //       (sum, dataset) =>
              //         sum + <number>dataset.data[context.dataIndex],
              //       0,
              //     );
              //     return [`${value}\n${dataServiceRef.round(value / total)}%`];
              //   },
            },
            tooltip: {
              enabled: true, // Enable the tooltip
              callbacks: {
                label: (context) => {
                  // Get the current value
                  const value: number = <number>context.raw;
                  const title: string = <string>context.dataset.label;

                  // Calculate the total for the current data index
                  const total = context.chart.data.datasets.reduce(
                    (sum, dataset) =>
                      sum + <number>dataset.data[context.dataIndex],
                    0,
                  );

                  // Calculate the percentage
                  const percentage = ((value / total) * 100).toFixed(1);

                  // Return the formatted tooltip text
                  return value === 0
                    ? ''
                    : [`${title} ${value}, ${percentage}%`];
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Courses',
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Enrolled (Count)',
              },
              ticks: {
                callback: function (value) {
                  return Number.isInteger(value) ? value : null; // Show only whole numbers
                },
              },
            },
          },
        },
      });

      this.aprFticCourseGradesChart = new Chart(
        'apr-first-year-course-grades-chart',
        {
          type: 'bar',
          data: {
            labels: this.data.apr_ftic_course_by_grade.map((c) => c.grade),
            datasets: [
              {
                label: 'APR',
                data: this.data.apr_ftic_course_by_grade.map((c) => c.num_apr),
                backgroundColor: 'rgba(0, 156, 222, 0.6)',
                borderColor: 'rgba(0, 156, 222, 1)',
                borderWidth: 1,
              },
              {
                label: 'Non-APR',
                data: this.data.apr_ftic_course_by_grade.map(
                  (c) => c.num_non_apr,
                ),
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
              },
            ],
          },
          options: {
            plugins: {
              datalabels: {
                color: '#000', // Text color
                anchor: 'center', // Position relative to bar (start, center, end)
                align: 'top', // Align text (top, middle, bottom)
              },
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Enrolled (Count)',
                },
                ticks: {
                  callback: function (value) {
                    return Number.isInteger(value) ? value : null; // Show only whole numbers
                  },
                  stepSize: 1, // Ensure it steps by 1 for whole numbers only
                },
              },
            },
          },
        },
      );

      this.passRateEnrollmentChart = new Chart('pass-rate-enrollment-chart', {
        data: {
          labels: this.data.enrollment_pass_rate_courses_by_program.map(
            (c) => c.cohort,
          ),
          datasets: [
            {
              label: 'Enrolled (Count)',
              data: this.data.enrollment_pass_rate_courses_by_program.map(
                (c) => c.num_enrolled,
              ),
              backgroundColor: 'rgba(0, 156, 222, 0.6)',
              borderColor: 'rgba(0, 156, 222, 1)',
              borderWidth: 1,
              yAxisID: 'yLeft',
              type: 'bar',
            },
            {
              label: 'Pass Rate',
              data: this.data.enrollment_pass_rate_courses_by_program.map(
                (c) => Math.round(c.pass_rate * 100) / 100,
              ),
              backgroundColor: 'rgba(153, 102, 255, 0.6)',
              borderColor: 'rgba(153, 102, 255, 1)',
              borderWidth: 1,
              type: 'line',
              yAxisID: 'yRight',
              fill: false,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              color: '#000', // Text color
              anchor: 'center', // Position relative to bar (start, center, end)
              align: 'bottom', // Align text (top, middle, bottom)
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Cohort',
              },
            },
            yLeft: {
              beginAtZero: true,
              position: 'left',
              title: {
                display: true,
                text: 'Enrolled (Count)',
              },
              ticks: {
                callback: function (value) {
                  return Number.isInteger(value) ? value : null; // Show only whole numbers
                },
                stepSize: 1, // Ensure it steps by 1 for whole numbers only
              },
            },
            yRight: {
              beginAtZero: true,
              position: 'right',
              title: {
                display: true,
                text: 'Pass Rate',
              },
            },
          },
        },
      });
    });
  }

  navigateToGlossaryOfTerms(termValue: string) {
    this.routeService.navigateNewTab('/glossary-of-terms', {
      term_value: termValue,
    });
  }
}
